import { addMinutes, isAfter } from 'date-fns';

/**
 * eSAV Exchange Store
 * ----------------------------
 *
 * Used to store data for exchange process
 *
 */
export const useExchangeStore = defineStore('exchange', () => {
    const { exchangeableGroups } = useLoadEsavBooking();

    const route = useRoute('esav-exchange-step-1');

    // Trip IDs from route query params
    const exchangeTripUpId = computed(() => route.query.tripUpId as string);
    const exchangeTripDownId = computed(() => route.query.tripDownId as string);

    // The exchange groups (previously transformed from booking), that match the given trip IDs
    const exchangeGroupTripUp = computed(() => exchangeableGroups.value.filter(eg => eg.type === 'Trip').find(eg => eg.id === exchangeTripUpId.value));
    const exchangeGroupTripDown = computed(() => exchangeableGroups.value.filter(eg => eg.type === 'Trip').find(eg => eg.id === exchangeTripDownId.value));

    // Original trips before exchange
    const originalTripUp = computed(() => exchangeGroupTripUp.value?.trip);
    const originalTripDown = computed(() => exchangeGroupTripDown.value?.trip);

    // Selected trips (to exchange to)
    const selectedTripUp = ref<Trip>();
    const selectedTripDown = ref<Trip>();

    // The currently selected trip (either selected or original)
    const displayedTripUp = computed(() => selectedTripUp.value ?? originalTripUp.value);
    const displayedTripDown = computed(() => selectedTripDown.value ?? originalTripDown.value);

    const isRoundtrip = computed(() => !!exchangeGroupTripDown.value && exchangeGroupTripDown.value.exchangeables.length > 0);
    const expandedSelector = ref<'tripUp' | 'tripDown' | null>(null);

    // If a new trip (for exchange) has been selected
    const isNewTripUpSelected = computed(() => !!selectedTripUp.value && selectedTripUp.value?.id !== originalTripUp.value?.id);
    const isNewTripDownSelected = computed(() => !!selectedTripDown.value && selectedTripDown.value?.id !== originalTripDown.value?.id);

    // Check for valid durations
    const hasValidStayDuration = computed((): boolean => {
        const MIN_STAY_DURATION = 30;
        return !isRoundtrip.value
            || (
                !!displayedTripUp.value?.arrivalTime
                && !!displayedTripDown.value?.departureTime
                && isAfter(
                    addMinutes(displayedTripDown.value?.departureTime, MIN_STAY_DURATION),
                    displayedTripUp.value?.arrivalTime,
                ));
    });

    /**
     * Exchanging
     * --------------------------------------------------------------------------------------------------
     *
     */
    const selectedExchangeOfferIds = computed(() => {
        // The backend returns one offer variant per overrule code (after-sales reason) per trip;
        // as specified by DSI, we should only pick and use the first offerId.
        // instead of eg. filtering by offer.appliedOverruleCode === 'CUSTOMER_CHANGE_OF_PLANS'
        const exchangeOfferIdUp = isNewTripUpSelected.value ? selectedTripUp.value?.offers?.[0]?.offerId : undefined;
        const exchangeOfferIdDown = isNewTripDownSelected.value ? selectedTripDown.value?.offers?.[0]?.offerId : undefined;

        return [
            exchangeOfferIdUp,
            exchangeOfferIdDown,
        ].filter(Boolean) as string[];
    });

    const exchangeCompletedOffers: Ref<ExchangeCompletedOffer[]> = ref([]);
    const exchangedTripUp = ref<Trip>();
    const exchangedTripDown = ref<Trip>();

    function setExchangeCompleted(offers: ExchangeCompletedOffer[]) {
        exchangeCompletedOffers.value = offers;
        exchangedTripUp.value = displayedTripUp.value;
        exchangedTripDown.value = displayedTripDown.value;
        selectedTripUp.value = undefined;
        selectedTripDown.value = undefined;
        expandedSelector.value = null;
    }

    /**
     * ------------------------------------------------------------------------------------------
     */

    function reset() {
        selectedTripUp.value = undefined;
        selectedTripDown.value = undefined;
        expandedSelector.value = null;
        exchangeCompletedOffers.value = [];
        exchangedTripUp.value = undefined;
        exchangedTripDown.value = undefined;
    }

    return {

        exchangeTripUpId,
        exchangeTripDownId,

        exchangeGroupTripUp,
        exchangeGroupTripDown,

        originalTripUp,
        originalTripDown,

        selectedTripUp,
        selectedTripDown,
        displayedTripUp,
        displayedTripDown,

        isRoundtrip,
        hasValidStayDuration,
        expandedSelector,

        selectedExchangeOfferIds,

        setExchangeCompleted,
        exchangeCompletedOffers,
        exchangedTripUp,
        exchangedTripDown,

        reset,
    };
}, {});
// Exchange store not persisted
